import React, { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiCloseCircleOutline } from "@mdi/js";

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  widthClass?: string;
  id?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  values,
  onChange,
  placeholder = "Select...",
  widthClass = "md:w-48",
  id,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggleValue = (v: string) => {
    if (values.includes(v)) {
      onChange(values.filter((x) => x !== v));
    } else {
      onChange([...values, v]);
    }
  };

  const label =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? (options.find((o) => o.value === values[0])?.label ?? values[0])
        : `${values.length} selected`;

  return (
    <div className="flex items-center gap-2" id={id}>
      <div ref={rootRef} className={`relative w-full ${widthClass}`}>
        <button
          type="button"
          id={id ? `${id}-button` : undefined}
          onClick={() => setOpen((p) => !p)}
          className="flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 pr-8 text-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer text-left"
        >
          <span className={values.length === 0 ? "text-gray-500" : ""}>
            {label}
          </span>
          <Icon
            path={mdiChevronDown}
            className="pointer-events-none absolute right-2 top-1/2 h-5 w-5 -translate-y-1/2 opacity-70"
          />
        </button>

        {open && (
          <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-md border bg-white shadow-lg">
            {options.map((opt) => {
              const checked = values.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  id={id ? `${id}-option-${opt.value}` : undefined}
                  className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleValue(opt.value)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
      {values.length > 0 && (
        <button
          onClick={() => onChange([])}
          className="text-gray-400 hover:text-red-500 transition-colors"
          title="Clear selection"
        >
          <Icon path={mdiCloseCircleOutline} size={0.7} />
        </button>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
